import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ClientConsolidationAPI } from '../services/clientConsolidation';
import { FormaPagamentoAPI } from '../services/formaPagamento';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { ServiceCostCentersAPI } from '../services/serviceCostCenters';

function formatValue(value, fallback = 'Não informado') {
  return value === null || value === undefined || String(value).trim() === '' ? fallback : String(value);
}

function formatLongValue(value, fallback = 'Sem observação') {
  const text = formatValue(value, fallback);
  return text.length > 180 ? `${text.slice(0, 177)}...` : text;
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(value) {
  if (!value) return 'Não informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Não informado' : date.toLocaleDateString('pt-BR');
}

function formatQuantity(value) {
  return Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function purchaseOrderKey(order = {}) {
  return String(order.blingPurchaseOrderId || order.id || order.externalId || '');
}

function paymentMethodLabel(method = {}) {
  return formatValue(method.descricao || method.nome || method.name || method.codigo || method.code || method.id, 'Forma sem descrição');
}

function proposalLink(payload = {}) {
  return formatValue(
    payload.integration?.link ||
    payload.integration?.url ||
    payload.integration?.response?.link ||
    payload.integration?.response?.url ||
    payload.integration?.response?.data?.link ||
    payload.integration?.response?.data?.url,
    ''
  );
}

function normalizePaymentMethods(payload = {}) {
  const rows = [payload.rows, payload.data, payload.items, payload.formasPagamento, payload.paymentMethods]
    .find((candidate) => Array.isArray(candidate)) || [];
  return rows.map((row) => {
    const source = row.formaPagamento || row.paymentMethod || row;
    const id = String(source.id || source.codigo || source.code || source.externalId || row.id || '').trim();
    if (!id) return null;
    return {
      ...row,
      id,
      descricao: source.descricao || source.description || row.descricao,
      nome: source.nome || source.name || row.nome,
      codigo: source.codigo || source.code || row.codigo,
      name: source.name || source.nome || source.descricao || source.description || row.name || row.descricao || id
    };
  }).filter(Boolean);
}

function proposalTitle(orders = []) {
  const numbers = orders.map((order) => order.blingPurchaseOrderNumber || order.blingPurchaseOrderId).filter(Boolean);
  if (!numbers.length) return 'Proposta comercial';
  return `Proposta comercial - PC ${numbers.slice(0, 4).join(', ')}${numbers.length > 4 ? '...' : ''}`;
}

function buildQuotationMessage(items = [], notes = '') {
  const selectedItems = items.filter((item) => item.selected);
  const lines = [
    'Olá! Poderia me enviar uma cotação dos itens abaixo?',
    ''
  ];
  selectedItems.forEach((item, index) => {
    lines.push(`${index + 1}. ${item.description} — ${formatQuantity(item.quantity)} ${item.unit || 'unidade'}`);
  });
  const safeNotes = String(notes || '').trim();
  if (safeNotes) lines.push('', safeNotes);
  lines.push(
    '',
    'Favor informar preço, prazo de entrega e condição de pagamento.',
    '',
    'Obrigado!'
  );
  return lines.join('\n');
}

function pricingDraftFromDetail(detail = {}) {
  const service = detail?.service || {};
  return {
    title: service.title || '',
    description: service.description || '',
    customerMasterClientId: service.customerMasterClientId?._id || service.customerMasterClientId || '',
    blingCustomerId: service.blingCustomerId || '',
    marginPercent: service.marginPercent ?? 20,
    bdiPercent: service.bdiPercent ?? 0,
    laborCost: service.laborCost ?? 0,
    travelCost: service.travelCost ?? 0,
    thirdPartyCost: service.thirdPartyCost ?? 0,
    extraCosts: service.extraCosts ?? 0
  };
}

function pricingDraftSignature(draft = {}) {
  return JSON.stringify({
    title: String(draft.title || ''),
    description: String(draft.description || ''),
    customerMasterClientId: String(draft.customerMasterClientId || ''),
    blingCustomerId: String(draft.blingCustomerId || ''),
    marginPercent: Number(draft.marginPercent || 0),
    bdiPercent: Number(draft.bdiPercent || 0),
    laborCost: Number(draft.laborCost || 0),
    travelCost: Number(draft.travelCost || 0),
    thirdPartyCost: Number(draft.thirdPartyCost || 0),
    extraCosts: Number(draft.extraCosts || 0)
  });
}

export default function ProposalFlow({ initialDetail, onUnauthorized, onPrepared, onFinished }) {
  const [step, setStep] = useState(initialDetail?.service?._id ? 2 : 1);
  const [detail, setDetail] = useState(initialDetail || null);
  const [orderFilters, setOrderFilters] = useState({ search: '', supplier: '', dateStart: '', dateEnd: '' });
  const [purchaseOrders, setPurchaseOrders] = useState({ rows: [], total: 0, collection: '' });
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [quotationList, setQuotationList] = useState(null);
  const [quotationItems, setQuotationItems] = useState([]);
  const [quotationNotes, setQuotationNotes] = useState('');
  const [quotationMessage, setQuotationMessage] = useState('');
  const [quotationCopied, setQuotationCopied] = useState(false);
  const [pricingDraft, setPricingDraft] = useState(() => pricingDraftFromDetail(initialDetail));
  const [saveState, setSaveState] = useState(initialDetail?.service?._id ? 'saved' : 'idle');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodSearch, setPaymentMethodSearch] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [preview, setPreview] = useState(null);
  const [createdProposal, setCreatedProposal] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const lastSavedPricingSignatureRef = useRef(pricingDraftSignature(pricingDraftFromDetail(initialDetail)));

  useEffect(() => {
    if (!initialDetail?.service?._id) return;
    const nextDraft = pricingDraftFromDetail(initialDetail);
    setDetail(initialDetail);
    setPricingDraft(nextDraft);
    lastSavedPricingSignatureRef.current = pricingDraftSignature(nextDraft);
    setSaveState('saved');
    setStep(2);
  }, [initialDetail]);

  useEffect(() => {
    if (!detail?.service?._id || step !== 2) return undefined;
    const currentSignature = pricingDraftSignature(pricingDraft);
    if (currentSignature === lastSavedPricingSignatureRef.current) return undefined;

    setSaveState('saving');
    const timeout = window.setTimeout(async () => {
      try {
        const payload = await ServiceCostCentersAPI.updatePricing(detail.service._id, {
          ...pricingDraft,
          marginPercent: Number(pricingDraft.marginPercent || 0),
          bdiPercent: Number(pricingDraft.bdiPercent || 0),
          laborCost: Number(pricingDraft.laborCost || 0),
          travelCost: Number(pricingDraft.travelCost || 0),
          thirdPartyCost: Number(pricingDraft.thirdPartyCost || 0),
          extraCosts: Number(pricingDraft.extraCosts || 0)
        });
        lastSavedPricingSignatureRef.current = currentSignature;
        setDetail(payload);
        setSaveState('saved');
        setError('');
        onPrepared?.(payload);
      } catch (requestError) {
        handleError(requestError, 'Não foi possível salvar e recalcular os custos.');
        setSaveState('error');
      }
    }, 700);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingDraft]);

  const selectedOrders = useMemo(
    () => (purchaseOrders.rows || []).filter((order) => selectedOrderIds.includes(purchaseOrderKey(order))),
    [purchaseOrders, selectedOrderIds]
  );

  const selectedTotal = selectedOrders.reduce((sum, order) => sum + Number(order.totalValue || 0), 0);
  const service = detail?.service || {};
  const items = detail?.items || [];
  const purchaseLinks = detail?.purchaseOrders || [];
  const estimatedMarginValue = Number(service.totalSaleValue || 0) - Number(service.totalCost || 0);
  const selectedPaymentMethod = paymentMethods.find((method) => String(method.id) === String(selectedPaymentMethodId));
  const previewPaymentMethodId = preview?.payload?.parcelas?.[0]?.formaPagamento?.id;
  const previewWarnings = preview?.warnings || [];
  const canCreate = Boolean(preview?.canCreate && (selectedPaymentMethodId || previewPaymentMethodId) && !createdProposal);

  function handleError(requestError, fallbackMessage) {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }
    setError(getErrorMessage(requestError, fallbackMessage));
  }

  async function searchOrders(event) {
    event?.preventDefault();
    setActionId('orders');
    setError('');
    try {
      let payload = await ServiceCostCentersAPI.purchaseOrders({ ...orderFilters, limit: 100 });
      if (!payload.rows?.length && orderFilters.search.trim()) {
        await ServiceCostCentersAPI.syncPurchaseOrders({
          number: orderFilters.search.trim(),
          supplier: orderFilters.supplier,
          dateStart: orderFilters.dateStart,
          dateEnd: orderFilters.dateEnd
        });
        payload = await ServiceCostCentersAPI.purchaseOrders({ ...orderFilters, limit: 100 });
      }
      setPurchaseOrders(payload);
      setSelectedOrderIds([]);
      setQuotationList(null);
      setQuotationItems([]);
      if (payload.collection && !payload.rows?.length) setError('Nenhum Pedido de Compra encontrado para os filtros informados.');
    } catch (requestError) {
      handleError(requestError, 'Não foi possível buscar Pedidos de Compra do Bling.');
    } finally {
      setActionId('');
    }
  }

  function toggleOrder(order) {
    const id = purchaseOrderKey(order);
    setSelectedOrderIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
    setQuotationList(null);
    setQuotationItems([]);
    setQuotationCopied(false);
  }

  async function generateQuotationList() {
    if (!selectedOrders.length) return;
    setActionId('quotation-list');
    setError('');
    setQuotationCopied(false);
    try {
      const payload = await ServiceCostCentersAPI.purchaseOrderQuotationList(selectedOrders);
      const rows = (payload.items || []).map((item) => ({ ...item, selected: true }));
      setQuotationList(payload);
      setQuotationItems(rows);
      setQuotationNotes('');
      setQuotationMessage(buildQuotationMessage(rows));
    } catch (requestError) {
      handleError(requestError, 'Não foi possível gerar a lista para cotação.');
    } finally {
      setActionId('');
    }
  }

  function closeQuotationList() {
    setQuotationList(null);
    setQuotationItems([]);
    setQuotationNotes('');
    setQuotationMessage('');
    setQuotationCopied(false);
  }

  function updateQuotationSelection(key, selected) {
    setQuotationItems((current) => {
      const next = current.map((item) => (item.key === key ? { ...item, selected } : item));
      setQuotationMessage(buildQuotationMessage(next, quotationNotes));
      setQuotationCopied(false);
      return next;
    });
  }

  function setAllQuotationItems(selected) {
    setQuotationItems((current) => {
      const next = current.map((item) => ({ ...item, selected }));
      setQuotationMessage(buildQuotationMessage(next, quotationNotes));
      setQuotationCopied(false);
      return next;
    });
  }

  function updateQuotationNotes(value) {
    setQuotationNotes(value);
    setQuotationMessage(buildQuotationMessage(quotationItems, value));
    setQuotationCopied(false);
  }

  async function copyQuotationMessage() {
    try {
      await navigator.clipboard.writeText(quotationMessage);
      setQuotationCopied(true);
    } catch (copyError) {
      const textarea = document.createElement('textarea');
      textarea.value = quotationMessage;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setQuotationCopied(true);
    }
  }

  async function prepareFlow() {
    if (!selectedOrders.length) return;
    setActionId('prepare');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.prepareProposalFlow({
        serviceCostCenterId: service._id,
        purchaseOrders: selectedOrders,
        title: pricingDraft.title || proposalTitle(selectedOrders),
        customerMasterClientId: pricingDraft.customerMasterClientId,
        blingCustomerId: pricingDraft.blingCustomerId,
        marginPercent: pricingDraft.marginPercent,
        bdiPercent: pricingDraft.bdiPercent
      });
      const nextDraft = pricingDraftFromDetail(payload);
      setDetail(payload);
      setPricingDraft(nextDraft);
      lastSavedPricingSignatureRef.current = pricingDraftSignature(nextDraft);
      setSaveState('saved');
      setStep(2);
      setPreview(null);
      setCreatedProposal(null);
      onPrepared?.(payload);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível preparar a proposta comercial.');
    } finally {
      setActionId('');
    }
  }

  async function searchClients(event) {
    event?.preventDefault();
    if (!clientSearch.trim()) return;
    setActionId('client-search');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.search(clientSearch.trim());
      const masters = (payload.masterClients || []).map((client) => ({
        id: client.id || client._id,
        name: client.name || client.corporateName || client.fantasyName,
        documentNumber: client.documentNumber,
        city: client.city,
        state: client.state,
        blingCustomerId: ''
      }));
      const linked = (payload.linkedRecords || [])
        .filter((record) => record.sourceSystem === 'bling' && record.linkedMasterClientId)
        .map((record) => ({
          id: record.linkedMasterClientId,
          name: record.linkedMasterClientName || record.name,
          documentNumber: record.documentNumber,
          city: record.city,
          state: record.state,
          blingCustomerId: record.externalId
        }));
      const seen = new Set();
      setClientResults([...linked, ...masters].filter((client) => {
        if (!client.id || seen.has(client.id)) return false;
        seen.add(client.id);
        return true;
      }));
    } catch (requestError) {
      handleError(requestError, 'Não foi possível buscar Cliente Mestre.');
    } finally {
      setActionId('');
    }
  }

  function selectClient(client) {
    setPricingDraft((current) => ({
      ...current,
      customerMasterClientId: client.id || '',
      blingCustomerId: client.blingCustomerId || current.blingCustomerId || ''
    }));
    setClientSearch(client.name || '');
    setClientResults([]);
  }

  async function searchPaymentMethods(event) {
    event?.preventDefault();
    setActionId('payment-methods');
    setError('');
    try {
      const payload = await FormaPagamentoAPI.listarFormasPagamento({ search: paymentMethodSearch.trim(), limit: 50 });
      const rows = normalizePaymentMethods(payload);
      setPaymentMethods(rows);
      if (rows.length === 1) setSelectedPaymentMethodId(rows[0].id);
      if (!rows.length) setError('Nenhuma forma de pagamento encontrada. Verifique a configuração padrão em Configurações > CEC Integra.');
    } catch (requestError) {
      handleError(requestError, 'Não foi possível carregar formas de pagamento.');
    } finally {
      setActionId('');
    }
  }

  async function updateItem(item, field, value) {
    if (!service._id) return;
    const numericFields = ['quantity', 'purchaseUnitValue', 'saleUnitValue'];
    const payload = { [field]: numericFields.includes(field) ? Number(value || 0) : value };
    if (field === 'includeInProposal') payload[field] = Boolean(value);
    setActionId(`item-${item._id}`);
    try {
      await ServiceCostCentersAPI.updateItem(service._id, item._id, payload);
      const refreshed = await ServiceCostCentersAPI.detail(service._id);
      setDetail(refreshed);
      onPrepared?.(refreshed);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível atualizar o item consolidado.');
    } finally {
      setActionId('');
    }
  }

  async function loadPreview() {
    if (!service._id) return;
    setActionId('preview');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.preview(service._id, selectedPaymentMethodId ? { paymentMethodId: selectedPaymentMethodId } : {});
      setPreview(payload);
      setSelectedPaymentMethodId(String(payload?.payload?.parcelas?.[0]?.formaPagamento?.id || selectedPaymentMethodId || ''));
      setStep(3);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível montar a prévia comercial.');
    } finally {
      setActionId('');
    }
  }

  async function createProposal() {
    if (!canCreate || actionId === 'create') return;
    setActionId('create');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.createBlingCommercialProposal(service._id, {
        paymentMethodId: selectedPaymentMethodId || previewPaymentMethodId
      });
      setCreatedProposal(payload);
      const refreshed = await ServiceCostCentersAPI.detail(service._id);
      setDetail(refreshed);
      onPrepared?.(refreshed);
      onFinished?.(payload);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível criar a Proposta Comercial no Bling.');
      if (requestError?.response?.data?.payload) setPreview(requestError.response.data.payload);
    } finally {
      setActionId('');
    }
  }

  return (
    <section className="proposal-flow">
      <div className="proposal-steps" aria-label="Etapas da proposta comercial">
        {['Selecionar Pedidos de Compra', 'Revisar custos e preço de venda', 'Conferir e criar a Proposta Comercial'].map((label, index) => (
          <button key={label} type="button" className={step === index + 1 ? 'active' : ''} onClick={() => setStep(index + 1)} disabled={index + 1 > step && !service._id}>
            <span>{index + 1}</span>{label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {step === 1 && (
        <div className="client-detail-section proposal-step-panel mt-3">
          <h3>Selecionar Pedidos de Compra</h3>
          <form className="proposal-form-grid proposal-form-grid--orders mt-3" onSubmit={searchOrders}>
            <div className="filter-control"><label className="form-label" htmlFor="proposal-order-number">Número do pedido</label><input id="proposal-order-number" className="form-control form-control-sm" value={orderFilters.search} onChange={(event) => setOrderFilters((current) => ({ ...current, search: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">Fornecedor</label><input className="form-control form-control-sm" value={orderFilters.supplier} onChange={(event) => setOrderFilters((current) => ({ ...current, supplier: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">De</label><input className="form-control form-control-sm" type="date" value={orderFilters.dateStart} onChange={(event) => setOrderFilters((current) => ({ ...current, dateStart: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">Até</label><input className="form-control form-control-sm" type="date" value={orderFilters.dateEnd} onChange={(event) => setOrderFilters((current) => ({ ...current, dateEnd: event.target.value }))} /></div>
            <button type="submit" className="btn btn-dark" disabled={actionId === 'orders'}>{actionId === 'orders' ? 'Buscando...' : 'Buscar compras'}</button>
          </form>
          <div className="proposal-selection-summary mt-3">
            <strong>{selectedOrders.length} selecionado(s)</strong>
            <span>{formatMoney(selectedTotal)}</span>
            <button type="button" className="btn btn-outline-primary" disabled={!selectedOrders.length || actionId === 'quotation-list'} onClick={generateQuotationList}>
              {actionId === 'quotation-list' ? 'Gerando...' : 'Gerar lista para cotação'}
            </button>
            <button type="button" className="btn btn-success" disabled={!selectedOrders.length || actionId === 'prepare'} onClick={prepareFlow}>
              {actionId === 'prepare' ? 'Preparando...' : 'Continuar para revisão'}
            </button>
          </div>
          <div className="client-table-wrap mt-3">
            <table className="client-audit-table">
              <thead><tr><th></th><th>Pedido</th><th>Fornecedor</th><th>Observação interna</th><th>Emissão</th><th>Status</th><th>Total</th></tr></thead>
              <tbody>
                {!purchaseOrders.rows?.length && <tr><td colSpan="7">Busque por número, fornecedor ou período para selecionar os Pedidos de Compra.</td></tr>}
                {purchaseOrders.rows?.map((order) => {
                  const key = purchaseOrderKey(order);
                  return (
                    <tr key={key}>
                      <td><input type="checkbox" checked={selectedOrderIds.includes(key)} onChange={() => toggleOrder(order)} /></td>
                      <td>{formatValue(order.blingPurchaseOrderNumber)}</td>
                      <td>{formatValue(order.supplierName)}</td>
                      <td className="purchase-order-notes" title={formatValue(order.internalNotes, '')}>{formatLongValue(order.internalNotes)}</td>
                      <td>{formatDate(order.issueDate)}</td>
                      <td>{formatValue(order.status)}</td>
                      <td>{formatMoney(order.totalValue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="client-detail-section proposal-step-panel proposal-step-panel--compact mt-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <h3>Revisar custos e preço de venda</h3>
            <span className={`proposal-save-state proposal-save-state--${saveState}`}>
              {saveState === 'saving' ? 'Salvando...' : saveState === 'saved' ? 'Valores atualizados' : saveState === 'error' ? 'Ajuste pendente' : 'Pronto'}
            </span>
          </div>
          <div className="client-readonly-grid proposal-summary-grid mt-2">
            <div><span>Total dos Pedidos de Compra</span><strong>{formatMoney(service.totalPurchaseCost)}</strong></div>
            <div><span>Custo total</span><strong>{formatMoney(service.totalCost)}</strong></div>
            <div><span>Preço de venda calculado</span><strong>{formatMoney(service.totalSaleValue)}</strong></div>
            <div><span>Margem estimada</span><strong>{formatMoney(estimatedMarginValue)}</strong></div>
          </div>
          <div className="proposal-form-grid proposal-form-grid--identity mt-3">
            <div className="filter-control filter-control--wide"><label className="form-label">Título</label><input className="form-control form-control-sm" value={pricingDraft.title} onChange={(event) => setPricingDraft((current) => ({ ...current, title: event.target.value }))} /></div>
            <div className="filter-control filter-control--wide"><label className="form-label">Observações</label><input className="form-control form-control-sm" value={pricingDraft.description} onChange={(event) => setPricingDraft((current) => ({ ...current, description: event.target.value }))} /></div>
          </div>
          <div className="proposal-form-grid proposal-pricing-grid mt-2">
            <div className="filter-control"><label className="form-label">Mão de obra</label><input className="form-control form-control-sm" type="number" value={pricingDraft.laborCost} onChange={(event) => setPricingDraft((current) => ({ ...current, laborCost: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">Deslocamento</label><input className="form-control form-control-sm" type="number" value={pricingDraft.travelCost} onChange={(event) => setPricingDraft((current) => ({ ...current, travelCost: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">Terceiros</label><input className="form-control form-control-sm" type="number" value={pricingDraft.thirdPartyCost} onChange={(event) => setPricingDraft((current) => ({ ...current, thirdPartyCost: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">Outros custos</label><input className="form-control form-control-sm" type="number" value={pricingDraft.extraCosts} onChange={(event) => setPricingDraft((current) => ({ ...current, extraCosts: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">Margem %</label><input className="form-control form-control-sm" type="number" value={pricingDraft.marginPercent} onChange={(event) => setPricingDraft((current) => ({ ...current, marginPercent: event.target.value }))} /></div>
            <div className="filter-control"><label className="form-label">BDI %</label><input className="form-control form-control-sm" type="number" value={pricingDraft.bdiPercent} onChange={(event) => setPricingDraft((current) => ({ ...current, bdiPercent: event.target.value }))} /></div>
          </div>
          <form className="proposal-form-grid proposal-client-row mt-3" onSubmit={searchClients}>
            <div className="filter-control filter-control--wide"><label className="form-label">Cliente</label><input className="form-control form-control-sm" value={clientSearch} onChange={(event) => setClientSearch(event.target.value)} placeholder="Nome, CNPJ, telefone ou e-mail" /></div>
            <button type="submit" className="btn btn-sm btn-dark" disabled={actionId === 'client-search'}>Buscar cliente</button>
          </form>
          {!!clientResults.length && (
            <div className="client-linked-list mt-2">
              {clientResults.map((client) => (
                <button type="button" key={client.id} className="client-existing-link" onClick={() => selectClient(client)}>
                  <strong>{formatValue(client.name)}</strong>
                  <span>{formatValue(client.documentNumber, 'Sem CNPJ')} · {formatValue([client.city, client.state].filter(Boolean).join('/'), 'Cidade não informada')}</span>
                </button>
              ))}
            </div>
          )}
          <details className="proposal-details proposal-details--compact mt-3" open={advancedOpen} onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}>
            <summary>Ver itens e ajustar valores</summary>
            <div className="client-table-wrap mt-3">
              <table className="client-audit-table">
                <thead><tr><th>Entra</th><th>Descrição comercial</th><th>Categoria</th><th>Qtd.</th><th>Custo un.</th><th>Venda un.</th><th>Total venda</th></tr></thead>
                <tbody>
                  {!items.length && <tr><td colSpan="7">Nenhum item consolidado ainda.</td></tr>}
                  {items.map((item) => (
                    <tr key={item._id}>
                      <td><input type="checkbox" checked={Boolean(item.includeInProposal)} onChange={(event) => updateItem(item, 'includeInProposal', event.target.checked)} /></td>
                      <td><input className="form-control form-control-sm" defaultValue={item.proposalDescription || item.description} onBlur={(event) => updateItem(item, 'proposalDescription', event.target.value)} /></td>
                      <td><select className="form-select form-select-sm" defaultValue={item.category || 'outros'} onChange={(event) => updateItem(item, 'category', event.target.value)}><option value="refrigeracao">Refrigeração</option><option value="eletrica">Elétrica</option><option value="gabinete">Gabinete</option><option value="compra_local">Compra local</option><option value="terceiro">Terceiro</option><option value="mao_de_obra">Mão de obra</option><option value="outros">Outros</option></select></td>
                      <td><input className="form-control form-control-sm" type="number" defaultValue={item.quantity || 0} onBlur={(event) => updateItem(item, 'quantity', event.target.value)} /></td>
                      <td><input className="form-control form-control-sm" type="number" defaultValue={item.purchaseUnitValue || 0} onBlur={(event) => updateItem(item, 'purchaseUnitValue', event.target.value)} /></td>
                      <td><input className="form-control form-control-sm" type="number" defaultValue={item.saleUnitValue || 0} onBlur={(event) => updateItem(item, 'saleUnitValue', event.target.value)} /></td>
                      <td>{formatMoney(item.saleTotalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          <div className="overlay-actions mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(1)}>Voltar</button>
            <button type="button" className="btn btn-success" disabled={!service._id || saveState === 'saving' || actionId === 'preview'} onClick={loadPreview}>Conferir proposta</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="client-detail-section proposal-step-panel mt-3">
          <h3>Conferir e criar Proposta Comercial</h3>
          {!!previewWarnings.length && <div className="alert alert-warning mt-3">{previewWarnings.join(' ')}</div>}
          <div className="client-readonly-grid mt-3">
            <div><span>Cliente</span><strong>{formatValue(preview?.customer?.name || service.customerMasterClientId?.name)}</strong></div>
            <div><span>Título</span><strong>{formatValue(service.title)}</strong></div>
            <div><span>Pedidos de origem</span><strong>{purchaseLinks.length}</strong></div>
            <div><span>Valor de venda</span><strong>{formatMoney(preview?.total || service.totalSaleValue)}</strong></div>
            <div><span>Margem</span><strong>{formatMoney(estimatedMarginValue)}</strong></div>
            <div><span>Itens enviados</span><strong>{preview?.payload?.itens?.length || 0}</strong></div>
          </div>
          <div className="client-detail-box mt-3">
            <h3>Forma de pagamento</h3>
            <form className="proposal-form-grid proposal-client-row" onSubmit={searchPaymentMethods}>
              <div className="filter-control filter-control--wide"><input className="form-control form-control-sm" value={paymentMethodSearch} onChange={(event) => setPaymentMethodSearch(event.target.value)} placeholder="Buscar por nome ou código" /></div>
              <button type="submit" className="btn btn-outline-secondary" disabled={actionId === 'payment-methods'}>Buscar</button>
            </form>
            {paymentMethods.length ? (
              <select className="form-select mt-2" value={selectedPaymentMethodId || previewPaymentMethodId || ''} onChange={(event) => setSelectedPaymentMethodId(event.target.value)}>
                <option value="">Usar padrão configurado</option>
                {paymentMethods.map((method) => <option key={method.id} value={method.id}>{paymentMethodLabel(method)}</option>)}
              </select>
            ) : (
              <p className="text-muted mb-0 mt-2">Configure a forma padrão em Configurações > CEC Integra ou use a busca acima.</p>
            )}
            <p className="text-muted mb-0 mt-2">Selecionada: {selectedPaymentMethod ? paymentMethodLabel(selectedPaymentMethod) : 'Padrão configurado'}</p>
          </div>
          <div className="client-linked-list mt-3">
            {purchaseLinks.map((order) => <article key={order._id || purchaseOrderKey(order)} className="client-linked-card"><div><strong>Pedido {formatValue(order.blingPurchaseOrderNumber)}</strong><span>{formatValue(order.supplierName)} · {formatDate(order.issueDate)} · {formatMoney(order.totalValue)}</span></div></article>)}
          </div>
          <details className="proposal-details mt-3" open={technicalOpen} onToggle={(event) => setTechnicalOpen(event.currentTarget.open)}>
            <summary>Detalhes técnicos</summary>
            <pre className="client-json-preview mt-3">{JSON.stringify({ service, preview, lastAttempt: service.metadata?.cecApiIntegra?.lastProposalAttempt }, null, 2)}</pre>
          </details>
          {createdProposal && (
            <div className="alert alert-success mt-3">
              Proposta criada com sucesso. Número {formatValue(detail?.service?.blingProposalNumber || createdProposal.integration?.response?.numero, 'não informado')}; ID Bling {formatValue(detail?.service?.blingProposalId || createdProposal.proposalId, 'não informado')}; valor {formatMoney(createdProposal.preview?.total || preview?.total)}; data {formatDate(new Date())}.
              {proposalLink(createdProposal) && <div><a href={proposalLink(createdProposal)} target="_blank" rel="noreferrer">Abrir proposta no Bling</a></div>}
            </div>
          )}
          <div className="overlay-actions mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(2)}>Voltar e corrigir</button>
            <button type="button" className="btn btn-success" disabled={!canCreate || actionId === 'create'} onClick={createProposal}>
              {actionId === 'create' ? 'Criando...' : 'Criar proposta no Bling'}
            </button>
          </div>
        </div>
      )}

      {quotationList && (
        <div className="task-overlay-backdrop">
          <aside className="task-overlay-panel client-detail-drawer shadow-lg quotation-list-modal">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Cotação por WhatsApp</div>
                <h2>Lista para cotação</h2>
                <p className="text-muted mb-0">{quotationList.purchaseOrders?.length || 0} Pedido(s) de Compra selecionado(s)</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={closeQuotationList}>Fechar</button>
            </div>

            <div className="client-detail-section mt-3">
              <h3>Pedidos selecionados</h3>
              <div className="client-linked-list">
                {quotationList.purchaseOrders?.map((order) => (
                  <article key={order.blingPurchaseOrderId} className="client-linked-card">
                    <div>
                      <strong>Pedido {formatValue(order.blingPurchaseOrderNumber)}</strong>
                      <span>{formatValue(order.supplierName)} · {formatLongValue(order.internalNotes)}</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="client-detail-section mt-3">
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <h3>Itens encontrados</h3>
                <div className="overlay-actions">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setAllQuotationItems(true)}>Selecionar todos</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setAllQuotationItems(false)}>Desmarcar todos</button>
                </div>
              </div>
              <div className="client-table-wrap mt-3">
                <table className="client-audit-table">
                  <thead><tr><th></th><th>Descrição</th><th>Qtd.</th><th>Unidade</th><th>Origens</th></tr></thead>
                  <tbody>
                    {!quotationItems.length && <tr><td colSpan="5">Nenhum item encontrado nos pedidos selecionados.</td></tr>}
                    {quotationItems.map((item) => (
                      <tr key={item.key}>
                        <td><input type="checkbox" checked={Boolean(item.selected)} onChange={(event) => updateQuotationSelection(item.key, event.target.checked)} /></td>
                        <td>{formatValue(item.description)}</td>
                        <td>{formatQuantity(item.quantity)}</td>
                        <td>{formatValue(item.unit, 'unidade')}</td>
                        <td>{item.origins?.length || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="client-detail-section mt-3">
              <div className="filter-control filter-control--wide">
                <label className="form-label">Observação opcional</label>
                <input className="form-control" value={quotationNotes} onChange={(event) => updateQuotationNotes(event.target.value)} placeholder="Ex.: informar frete separado" />
              </div>
              <div className="filter-control filter-control--wide mt-3">
                <label className="form-label">Mensagem para WhatsApp</label>
                <textarea className="form-control quotation-message-preview" value={quotationMessage} onChange={(event) => { setQuotationMessage(event.target.value); setQuotationCopied(false); }} rows="9" />
              </div>
              <div className="overlay-actions mt-3">
                <button type="button" className="btn btn-success" disabled={!quotationMessage.trim()} onClick={copyQuotationMessage}>Copiar para WhatsApp</button>
                {quotationCopied && <span className="text-success">Texto copiado.</span>}
              </div>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
